// src/services/auth.js
import api from './api';

export const login = async (credentials) => {
    try {
        const formData = new URLSearchParams();
        formData.append('username', credentials.email);
        formData.append('password', credentials.password);

        const response = await api.post('/token', formData.toString(), {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });

        if (response.data && response.data.access_token) {
            localStorage.setItem('accessToken', response.data.access_token);

            if (response.data.user) {
                // Convert is_admin to proper boolean and add debug logging
                const userData = {
                    ...response.data.user,
                    is_admin: Boolean(response.data.user.is_admin) // Convert 1/0 to true/false
                };

                // Debug logging to check the data
                console.log('🔍 Original user data from API:', response.data.user);
                console.log('🔍 Processed user data:', userData);
                console.log('🔍 is_admin value:', userData.is_admin, typeof userData.is_admin);

                localStorage.setItem('user', JSON.stringify(userData));
            }
        }

        return response.data;
    } catch (error) {
        if (error.response && error.response.data && error.response.data.detail) {
            console.error('Login failed:', error.response.data.detail);

            // Handle specific error messages
            if (error.response.status === 403) {
                throw new Error('Your account has been disabled. Please contact the administrator.');
            }

            throw new Error(error.response.data.detail);
        } else {
            console.error('Unexpected login error:', error.message || error);
            throw new Error('An unexpected error occurred during login.');
        }
    }
};

// Register new user
export const register = async (userData) => {
    try {
        const response = await api.post('/register', userData);
        return response.data;
    } catch (error) {
        console.error('Registration error:', error);
        if (error.response?.status === 400) {
            throw new Error('Email already registered');
        }
        throw new Error(error.response?.data?.detail || 'Registration failed');
    }
};

// Logout user
export const logout = () => {
    // Remove tokens from localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    console.log('User logged out');
};

// Helper function to check if user is authenticated
export const isAuthenticated = () => {
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('user');

    // Check if both token and user data exist
    if (!token || !user) {
        return false;
    }

    // Optionally validate token format
    try {
        JSON.parse(user);
        return true;
    } catch (e) {
        console.error('Invalid user data in localStorage:', e);
        // Clean up corrupted data
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        return false;
    }
};

// Get current user data
export const getCurrentUser = () => {
    if (!isAuthenticated()) {
        return null;
    }

    const userStr = localStorage.getItem('user');
    if (!userStr) return null;

    try {
        const user = JSON.parse(userStr);
        console.log('🔍 getCurrentUser:', user);
        console.log('🔍 is_admin:', user.is_admin, typeof user.is_admin);
        return user;
    } catch (e) {
        console.error('Error parsing user data', e);
        // Clean up corrupted data
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        return null;
    }
};

// Validate token with server (optional but recommended)
export const validateToken = async () => {
    if (!isAuthenticated()) {
        return false;
    }

    try {
        const response = await api.get('/health'); // or any protected endpoint
        return response.status === 200;
    } catch (error) {
        if (error.response?.status === 401) {
            // Token is invalid, clear it
            logout();
            return false;
        }
        // Network error, assume token is still valid
        return true;
    }
};

// Initialize auth state on app load
export const initializeAuth = () => {
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('user');

    console.log('🔍 Initializing auth...');
    console.log('🔍 Token exists:', !!token);
    console.log('🔍 User data exists:', !!user);

    if (token && user) {
        try {
            const userData = JSON.parse(user);
            console.log('🔍 Auth initialized with user:', userData);
            return userData;
        } catch (e) {
            console.error('🔍 Failed to parse user data, clearing auth');
            logout();
            return null;
        }
    }

    console.log('🔍 No valid auth data found');
    return null;
};

export default {
    login,
    register,
    logout,
    isAuthenticated,
    getCurrentUser,
    validateToken,
    initializeAuth
};